import React, { useState, useEffect } from 'react';
import { reportsApi } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    BarChart3,
    RefreshCw,
    Download,
    Calendar,
    TrendingUp,
    CheckCircle2,
    Thermometer,
    AlertTriangle,
    FileText,
    Mail,
    Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { getDateRange, formatDate, cn } from '../lib/utils';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    LineChart,
    Line
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#D4AF37', '#10B981', '#3B82F6', '#F59E0B'];

const Reports = () => {
    const [loading, setLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [trainingStatus, setTrainingStatus] = useState(null);
    const [startDate, setStartDate] = useState(getDateRange(30).start);
    const [endDate, setEndDate] = useState(getDateRange(30).end);

    const getToken = () => localStorage.getItem('token');

    useEffect(() => {
        loadTrainingStatus();
    }, []);

    const loadTrainingStatus = async () => {
        try {
            const headers = { Authorization: `Bearer ${getToken()}` };
            const response = await axios.get(`${BACKEND_URL}/api/notifications/training-status`, { headers });
            setTrainingStatus(response.data);
        } catch (error) {
            console.error('Failed to load training status:', error);
        }
    };

    const loadReport = async () => {
        setLoading(true);
        try {
            const response = await reportsApi.getCompliance(startDate, endDate);
            setReport(response.data);
        } catch (error) {
            console.error('Failed to load report:', error);
            toast.error('Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const exportPDF = async () => {
        setPdfLoading(true);
        try {
            const headers = { Authorization: `Bearer ${getToken()}` };
            const response = await axios.get(
                `${BACKEND_URL}/api/reports/compliance/pdf?start_date=${startDate}&end_date=${endDate}`,
                { headers, responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `compliance-report-${startDate}-to-${endDate}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF downloaded successfully');
        } catch (error) {
            console.error('Failed to export PDF:', error);
            toast.error('Failed to generate PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const sendTrainingNotifications = async () => {
        setEmailLoading(true);
        try {
            const headers = { Authorization: `Bearer ${getToken()}` };
            const response = await axios.post(`${BACKEND_URL}/api/notifications/training-expiry`, {}, { headers });
            const data = response.data;
            toast.success(`Sent ${data.emails_sent} notification(s). ${data.emails_failed} failed.`);
            loadTrainingStatus();
        } catch (error) {
            console.error('Failed to send notifications:', error);
            toast.error('Failed to send notifications');
        } finally {
            setEmailLoading(false);
        }
    };

    const exportCSV = () => {
        if (!report) return;

        const rows = [
            ['Compliance Report', `${startDate} to ${endDate}`],
            [],
            ['Checklists'],
            ['Total', report.checklists.total],
            ['Completed', report.checklists.completed],
            ['Completion Rate', `${report.checklists.completion_rate}%`],
            [],
            ['Temperature Monitoring'],
            ['Total Logs', report.temperature.total_logs],
            ['Acceptable', report.temperature.acceptable],
            ['Issues', report.temperature.issues],
            ['Compliance Rate', `${report.temperature.compliance_rate}%`],
            [],
            ['Incidents'],
            ['Total', report.incidents.total],
            ['Resolved', report.incidents.resolved],
            ['Open', report.incidents.open],
            [],
            ['Daily Breakdown'],
            ['Date', 'Total Checklists', 'Completed'],
            ...report.daily_data.map(d => [d.date, d.total, d.completed])
        ];

        const csvContent = rows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compliance-report-${startDate}-to-${endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Report exported successfully');
    };

    const checklistTypeData = report?.checklists?.by_type 
        ? Object.entries(report.checklists.by_type).map(([name, data]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            total: data.total,
            completed: data.completed,
            rate: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0
        }))
        : [];

    const pieData = report ? [
        { name: 'Completed', value: report.checklists.completed },
        { name: 'Pending', value: report.checklists.total - report.checklists.completed }
    ] : [];

    return (
        <Layout>
            <div className="space-y-6" data-testid="reports-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Reports & Analytics</h1>
                        <p className="text-slate-400 mt-1">
                            Compliance reports and performance metrics
                        </p>
                    </div>
                </div>

                {/* Date Selection */}
                <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row items-end gap-4">
                            <div className="space-y-2 flex-1">
                                <Label className="text-slate-300">Start Date</Label>
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="bg-slate-950 border-slate-800 text-slate-200"
                                    data-testid="report-start-date"
                                />
                            </div>
                            <div className="space-y-2 flex-1">
                                <Label className="text-slate-300">End Date</Label>
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="bg-slate-950 border-slate-800 text-slate-200"
                                    data-testid="report-end-date"
                                />
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <Button
                                    onClick={loadReport}
                                    disabled={loading}
                                    className="brand-btn"
                                    data-testid="generate-report-button"
                                >
                                    {loading ? (
                                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <BarChart3 className="h-4 w-4 mr-2" />
                                    )}
                                    Generate Report
                                </Button>
                                {report && (
                                    <>
                                        <Button
                                            onClick={exportCSV}
                                            variant="outline"
                                            className="border-slate-700 text-slate-300 hover:bg-slate-800"
                                            data-testid="export-csv-button"
                                        >
                                            <Download className="h-4 w-4 mr-2" />
                                            CSV
                                        </Button>
                                        <Button
                                            onClick={exportPDF}
                                            disabled={pdfLoading}
                                            variant="outline"
                                            className="border-slate-700 text-slate-300 hover:bg-slate-800"
                                            data-testid="export-pdf-button"
                                        >
                                            {pdfLoading ? (
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <FileText className="h-4 w-4 mr-2" />
                                            )}
                                            PDF
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Training Notifications Card */}
                {trainingStatus && trainingStatus.total_requiring_attention > 0 && (
                    <Card className="bg-amber-500/10 border-amber-500/20">
                        <CardContent className="py-4">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <Bell className="h-5 w-5 brand-icon" />
                                    <div>
                                        <p className="text-amber-200 font-medium">
                                            Training Certifications Requiring Attention
                                        </p>
                                        <p className="text-amber-300/70 text-sm">
                                            {trainingStatus.expired} expired, {trainingStatus.expiring_soon} expiring soon
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    onClick={sendTrainingNotifications}
                                    disabled={emailLoading}
                                    className="brand-btn"
                                    data-testid="send-training-notifications"
                                >
                                    {emailLoading ? (
                                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Mail className="h-4 w-4 mr-2" />
                                    )}
                                    Send Email Notifications
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {report && (
                    <>
                        {/* Summary Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-slate-400">Checklist Completion</p>
                                            <p className="text-3xl font-bold text-slate-100">{report.checklists.completion_rate}%</p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                {report.checklists.completed}/{report.checklists.total} completed
                                            </p>
                                        </div>
                                        <div className={cn(
                                            'h-12 w-12 rounded-xl flex items-center justify-center',
                                            report.checklists.completion_rate >= 90 ? 'bg-emerald-500/10' :
                                            report.checklists.completion_rate >= 70 ? 'bg-amber-500/10' : 'bg-rose-500/10'
                                        )}>
                                            <CheckCircle2 className={cn(
                                                'h-6 w-6',
                                                report.checklists.completion_rate >= 90 ? 'text-emerald-400' :
                                                report.checklists.completion_rate >= 70 ? 'text-amber-400' : 'text-rose-400'
                                            )} />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-slate-400">Temperature Compliance</p>
                                            <p className="text-3xl font-bold text-slate-100">{report.temperature.compliance_rate}%</p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                {report.temperature.acceptable}/{report.temperature.total_logs} acceptable
                                            </p>
                                        </div>
                                        <div className={cn(
                                            'h-12 w-12 rounded-xl flex items-center justify-center',
                                            report.temperature.compliance_rate >= 95 ? 'bg-emerald-500/10' :
                                            report.temperature.compliance_rate >= 80 ? 'bg-amber-500/10' : 'bg-rose-500/10'
                                        )}>
                                            <Thermometer className={cn(
                                                'h-6 w-6',
                                                report.temperature.compliance_rate >= 95 ? 'text-emerald-400' :
                                                report.temperature.compliance_rate >= 80 ? 'text-amber-400' : 'text-rose-400'
                                            )} />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-slate-400">Temperature Issues</p>
                                            <p className="text-3xl font-bold text-slate-100">{report.temperature.issues}</p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                out of range readings
                                            </p>
                                        </div>
                                        <div className={cn(
                                            'h-12 w-12 rounded-xl flex items-center justify-center',
                                            report.temperature.issues === 0 ? 'bg-emerald-500/10' : 'bg-rose-500/10'
                                        )}>
                                            <AlertTriangle className={cn(
                                                'h-6 w-6',
                                                report.temperature.issues === 0 ? 'text-emerald-400' : 'text-rose-400'
                                            )} />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-slate-900 border-slate-800">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-slate-400">Incidents</p>
                                            <p className="text-3xl font-bold text-slate-100">{report.incidents.total}</p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                {report.incidents.resolved} resolved, {report.incidents.open} open
                                            </p>
                                        </div>
                                        <div className={cn(
                                            'h-12 w-12 rounded-xl flex items-center justify-center',
                                            report.incidents.open === 0 ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                                        )}>
                                            <TrendingUp className={cn(
                                                'h-6 w-6',
                                                report.incidents.open === 0 ? 'text-emerald-400' : 'text-amber-400'
                                            )} />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Charts */}
                        <div className="grid lg:grid-cols-2 gap-6">
                            {/* Daily Compliance Trend */}
                            <Card className="bg-slate-900 border-slate-800">
                                <CardHeader>
                                    <CardTitle className="text-lg text-slate-100">Daily Compliance Trend</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {report.daily_data.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={report.daily_data}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    stroke="#94A3B8"
                                                    tick={{ fill: '#94A3B8', fontSize: 12 }}
                                                    tickFormatter={(value) => value.slice(5)}
                                                />
                                                <YAxis stroke="#94A3B8" tick={{ fill: '#94A3B8', fontSize: 12 }} />
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: '#1E293B', 
                                                        border: '1px solid #334155',
                                                        borderRadius: '8px'
                                                    }}
                                                    labelStyle={{ color: '#F8FAFC' }}
                                                />
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="total" 
                                                    stroke="#D4AF37" 
                                                    strokeWidth={2}
                                                    name="Total"
                                                />
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="completed" 
                                                    stroke="#10B981" 
                                                    strokeWidth={2}
                                                    name="Completed"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-[300px] flex items-center justify-center text-slate-500">
                                            No data available for this period
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Checklist Completion */}
                            <Card className="bg-slate-900 border-slate-800">
                                <CardHeader>
                                    <CardTitle className="text-lg text-slate-100">Completion Overview</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {report.checklists.total > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={100}
                                                    fill="#8884d8"
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: '#1E293B', 
                                                        border: '1px solid #334155',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-[300px] flex items-center justify-center text-slate-500">
                                            No checklist data available
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Checklist by Type */}
                            {checklistTypeData.length > 0 && (
                                <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-slate-100">Completion by Checklist Type</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={checklistTypeData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                                <XAxis 
                                                    dataKey="name" 
                                                    stroke="#94A3B8"
                                                    tick={{ fill: '#94A3B8', fontSize: 12 }}
                                                />
                                                <YAxis stroke="#94A3B8" tick={{ fill: '#94A3B8', fontSize: 12 }} />
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: '#1E293B', 
                                                        border: '1px solid #334155',
                                                        borderRadius: '8px'
                                                    }}
                                                    labelStyle={{ color: '#F8FAFC' }}
                                                />
                                                <Legend />
                                                <Bar dataKey="total" fill="#D4AF37" name="Total" radius={[4, 4, 0, 0]} />
                                                <Bar dataKey="completed" fill="#10B981" name="Completed" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </>
                )}

                {!report && !loading && (
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="py-12">
                            <div className="empty-state">
                                <BarChart3 className="empty-state-icon" />
                                <p className="empty-state-title">Generate a Report</p>
                                <p className="empty-state-description">
                                    Select a date range and click "Generate Report" to view compliance analytics.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default Reports;
